import React from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {useRegisterSteps} from '../../hooks';
import {PATHS} from 'Routes';
import {LocationStates} from '../VerifyModalScreen/VerifyModalScreen';
import {SelfieLocationStateType} from 'views/VerifyModalScreen/SelfieIdentity/SelfieIdentity';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import tryAgainIcon from 'assets/status-something-wrong-icon.png';
import successIcon from 'assets/img/status-success-icon.png';
import styles from './ConfirmIdentityResultScreen.module.sass';
import Link from 'components/acommon/Link';
import Button from 'components/acommon/Button';

export type ResultLocationStateType = SelfieLocationStateType & {
  biomatch?: {
    biomatch_passed: boolean;
    biomatch_doc: string;
    biomatch_selfie: string;
  };
  isVerifiedOrRetake?: boolean;
};

export const getBiometricResultPayload = (
  location: {state: ResultLocationStateType},
  isMatch: boolean,
) => {
  return {
    biomatch_passed: isMatch || location.state?.biomatch?.biomatch_passed,
    biomatch_doc:
      location.state?.frontSideDocumentPhoto?.checkId ||
      location.state?.biomatch?.biomatch_doc,
    biomatch_selfie:
      location.state?.selfiePhoto?.checkId || location.state?.biomatch?.biomatch_selfie,
  };
};

export const getPersistedState = (location: {state: ResultLocationStateType}) => {
  const biomatchPayload = getBiometricResultPayload(
    location,
    location.state?.selfiePhoto?.result?.is_match,
  );
  return {
    ...location.state,
    isRetryBiomatch: Boolean(location.state?.selfiePhoto?.isRetryBiomatch),
    biomatch: {
      ...biomatchPayload,
    },
  };
};

function ConfirmIdentityResultScreen() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation<ResultLocationStateType>();
  const {reservation, isVerifyOnlyDocument, isVerifyDocumentAndSelfie} =
    useComputedReservationDetails();
  const {count, getCurrentStep} = useRegisterSteps();
  const identityVerificationStates =
    location.state?.[LocationStates.identityVerificationState] || {};
  const isMatch =
    (isVerifyOnlyDocument && identityVerificationStates.documentsPassed) ||
    (isVerifyDocumentAndSelfie && identityVerificationStates.biomatchPassed);
  const isRetryBiomatch = Boolean(location.state?.selfiePhoto?.isRetryBiomatch);

  const areBiomatchRetriesEnabled = Boolean(reservation?.are_biomatch_retries_enabled);

  const canRetriesBiomatch =
    (!isMatch && areBiomatchRetriesEnabled) || (!isMatch && isRetryBiomatch);

  const goBackToDocument = React.useCallback(() => {
    history.push(PATHS.verificationDocumentFront, location.state);
  }, [history, location.state]);

  const goNext = React.useCallback(() => {
    const state = getPersistedState(location);

    history.push(PATHS.verificationStart, state);
  }, [history, location]);

  React.useLayoutEffect(
    function closeModalIfVerified() {
      if (isMatch) goNext();
    },
    [isMatch, goNext],
  );

  React.useEffect(() => {
    const hasPersistedState = location.state?.[LocationStates.identityVerificationState];

    if (!hasPersistedState) {
      goBackToDocument();
    }
  }, [goBackToDocument, location.state]);

  const getTitle = () => {
    if (isMatch) {
      return t('verified');
    }
    return t('verification_pending');
  };

  const getSubtitle = () => {
    if (!areBiomatchRetriesEnabled && !isMatch) {
      return t('identetify_verification_error_can_proceed');
    }

    if (isMatch) {
      return t('your_identity_verified_successfully');
    }

    return t('photo_dont_match_but_continue');
  };

  return (
    <div className={styles.content}>
      <img
        className={styles.statusImage}
        alt="Something went wrong"
        src={isMatch ? successIcon : tryAgainIcon}
      ></img>
      <div className={styles.statusTitle}>{getTitle()}</div>
      <div className={styles.statusSubtitle}>{getSubtitle()}</div>
      <div className={styles.buttonsWrapper}>
        <div className={styles.buttonsWrapperMobile}>
          <div className={styles.progress}>{`${getCurrentStep(
            PATHS.verificationResult,
          )}/${count}`}</div>
          <Button rightIcon="arrow-next" className={styles.statusButton} onClick={goNext}>
            {t('ok')}
          </Button>
        </div>
        {canRetriesBiomatch && (
          <Link to={PATHS.verificationDocumentFront} className={styles.statusLink}>
            {t('try_again')}
          </Link>
        )}
      </div>
    </div>
  );
}

export {ConfirmIdentityResultScreen};
